/**
 * Set a new password, at the end of a reset link.
 *
 * ## How the link becomes a session
 *
 * The browser client runs auth-js's default `flowType: 'implicit'` (`supabase.ts` overrides neither
 * that nor `detectSessionInUrl`), so GoTrue's recovery mail points at its own `/auth/v1/verify` and
 * that redirects here with the tokens in the URL **fragment**: `#access_token=…&type=recovery`. A
 * fragment never leaves the browser, which is why the credential in a reset link is not in any
 * server log. auth-js consumes it while the client initialises, emits `PASSWORD_RECOVERY`, and then
 * clears the fragment. Implicit is also the flow that survives the ordinary case of asking on a
 * phone and opening the mail on a laptop — PKCE would need the code verifier from the browser that
 * asked.
 *
 * ## Why the fragment is read before anything else touches it
 *
 * `useState`'s initialiser runs during the first render, and the Supabase client is not constructed
 * until the effect below calls `getClient()` — so this reads the fragment while it is still there.
 * That ordering is what `LANDING` depends on, and the `PASSWORD_RECOVERY` subscription is the belt
 * to its braces: either piece of evidence is enough, so a future change that creates the client
 * earlier degrades to the event rather than to a broken screen.
 *
 * A spent or expired link is the other half. GoTrue sends that back as `#error=…&error_code=…`, and
 * auth-js clears the fragment ONLY on the success path — so the refusal is still there to read, and
 * it is rendered as itself with a way to ask for another link, rather than as a blank form that
 * fails on submit (the dead-end ban, ux.md §12).
 *
 * ## What this screen will not do
 *
 * It will not accept a password for an ordinary signed-in session. A session alone would be enough
 * to make `updateUser` succeed, and treating it as enough would turn this URL into "change the
 * password of whoever is signed in on this browser, without knowing the old one". Evidence of a
 * RECOVERY is required, which is what a reset link — and only a reset link — carries.
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Callout, Card, Field, LoadingState } from '@flowza/ui'
import { configStatus, getClient } from '../../supabase'
import {
  PASSWORD_HINT,
  TOO_SHORT_MESSAGE,
  property,
  tooShort as isTooShort,
  weakPasswordMessage,
} from '../../auth/password-failure'
import { AuthLayout } from './AuthLayout'

/** What GoTrue put in the fragment when it sent the browser here. */
export type Landing =
  | { readonly kind: 'recovery' }
  | { readonly kind: 'refused'; readonly message: string }
  | { readonly kind: 'none' }

/**
 * GoTrue's own words for a link that will not work, mapped to ours. `otp_expired` is the one a
 * caller actually meets: recovery links are single-use and short-lived, so the second click on the
 * same link — or a click the next morning — lands here.
 */
const REFUSAL: Readonly<Record<string, string>> = {
  otp_expired: 'That link has expired or has already been used.',
  access_denied: 'That link has expired or has already been used.',
}

const REFUSAL_FALLBACK = 'That link is no longer valid.'

/**
 * Reads the fragment GoTrue redirected with. Exported because the states this screen can be in are
 * decided entirely by this string, and a browser is a poor place to enumerate them.
 */
export function parseLanding(hash: string): Landing {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  const errorCode = params.get('error_code') ?? params.get('error')
  if (errorCode !== null) {
    return { kind: 'refused', message: REFUSAL[errorCode] ?? REFUSAL_FALLBACK }
  }
  // `type` is what separates a recovery from any other implicit-grant redirect that might land on
  // this path; the token is what makes it usable.
  if (params.get('type') === 'recovery' && params.get('access_token') !== null) {
    return { kind: 'recovery' }
  }
  return { kind: 'none' }
}

type State =
  /** The fragment says recovery; waiting for auth-js to turn it into a session. */
  | { readonly kind: 'opening' }
  | { readonly kind: 'ready' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'done' }
  | { readonly kind: 'unusable'; readonly message: string }

const NO_LINK =
  'Open the most recent link from your reset email. Links are single-use, so an older one will not work.'

/** The fragment looked like a recovery and still produced no session — almost always a spent link. */
const STALE =
  'That link did not open. It has most likely been used already or expired — ask for a fresh one.'

export default function ResetConfirm() {
  const navigate = useNavigate()
  const config = configStatus()
  // Read during the first render, before the effect below constructs the client that clears it.
  const [landing] = useState<Landing>(() =>
    typeof window === 'undefined' ? { kind: 'none' } : parseLanding(window.location.hash),
  )
  // Every reason this screen cannot proceed is knowable before the first paint, so it is decided
  // here rather than by an effect that would flash a spinner on its way to the same answer.
  // `configStatus()` and `getClient()` read the same two variables, which is why the effect's null
  // check below cannot fire once this branch has passed.
  const [state, setState] = useState<State>(() => {
    if (landing.kind === 'refused') return { kind: 'unusable', message: landing.message }
    if (landing.kind === 'none') return { kind: 'unusable', message: NO_LINK }
    if (!config.ok) {
      return {
        kind: 'unusable',
        message: `This deployment is not configured. Missing build variables: ${config.missing.join(', ')}.`,
      }
    }
    return { kind: 'opening' }
  })
  const [password, setPassword] = useState('')
  const [passwordFailure, setPasswordFailure] = useState<string | null>(null)

  useEffect(() => {
    if (landing.kind !== 'recovery') return
    const client = getClient()
    // Unreachable once the initialiser above has settled on `opening`; kept because `getClient`
    // is typed nullable and narrowing it here is cheaper than asserting it away.
    if (client === null) return
    let live = true
    // Belt to the fragment's braces: if anything ever consumes the fragment before the first
    // render, the event is still proof that this session came from a reset link.
    const { data: listener } = client.auth.onAuthStateChange((event) => {
      if (live && event === 'PASSWORD_RECOVERY') setState({ kind: 'ready' })
    })
    // `getSession` awaits the client's initialisation, which is where the fragment is exchanged —
    // so by the time this resolves the recovery session either exists or never will.
    void client.auth.getSession().then(({ data }) => {
      if (!live) return
      // Never downgrade: if `PASSWORD_RECOVERY` already arrived, the evidence is in and a session
      // read that has not caught up must not take it back. Whichever proof lands first wins, and
      // the other is a no-op — which is the point of listening for both.
      // `landing` is a recovery by the guard above, so the only thing a missing session can mean
      // here is that the token in it was already spent.
      setState((current) =>
        current.kind !== 'opening'
          ? current
          : data.session
            ? { kind: 'ready' }
            : { kind: 'unusable', message: STALE },
      )
    })
    return () => {
      live = false
      listener.subscription.unsubscribe()
    }
  }, [landing])

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const client = getClient()
    if (client === null) return
    setState({ kind: 'saving' })
    setPasswordFailure(null)
    const { error } = await client.auth.updateUser({ password })
    if (error) {
      const weak = weakPasswordMessage(error)
      if (weak !== null) {
        setPasswordFailure(weak)
        setState({ kind: 'ready' })
        return
      }
      // The other refusal that actually happens here is a session that expired while the form sat
      // open. It is not something a retype fixes, so it says so and offers the way back.
      const code = property(error, 'code')
      setState({
        kind: 'unusable',
        message:
          code === 'session_expired' || code === 'session_not_found'
            ? 'That link expired while this page was open.'
            : 'We could not set that password. Please ask for a fresh link and try again.',
      })
      return
    }
    setState({ kind: 'done' })
  }

  const tooShort = isTooShort(password)
  const passwordError = tooShort ? TOO_SHORT_MESSAGE : passwordFailure

  if (state.kind === 'done') {
    return (
      <AuthLayout title="Password changed" subtitle="You can use it from now on.">
        <Card>
          <Callout tone="info" title="That is done">
            Your password has been changed.
          </Callout>
          <Button className="mt-4 w-full" onClick={() => navigate('/app', { replace: true })}>
            Go to the console
          </Button>
        </Card>
      </AuthLayout>
    )
  }

  if (state.kind === 'unusable') {
    return (
      <AuthLayout
        title="That link will not open"
        subtitle="Reset links are single-use and expire quickly."
        footer={
          <>
            Remembered it?{' '}
            <Link to="/auth/login" className="text-accent font-medium">
              Sign in
            </Link>
          </>
        }
      >
        <Card>
          {/* Never a dead end: the only thing to do about a spent link is get another (ux.md §12). */}
          <Callout tone="danger" title="This link cannot be used">
            {state.message}
          </Callout>
          <Link to="/auth/reset">
            <Button className="mt-4 w-full">Send me a new link</Button>
          </Link>
        </Card>
      </AuthLayout>
    )
  }

  if (state.kind === 'opening') {
    return (
      <AuthLayout title="Set a new password" subtitle="Checking your link.">
        <Card>
          <LoadingState label="Opening your reset link" />
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="This replaces the password on your account. Choose one you have not used elsewhere."
    >
      {!config.ok ? (
        <div className="mb-4">
          <Callout tone="danger" title="This deployment is not configured">
            Missing build variables: {config.missing.join(', ')}. Changing a password needs them.
          </Callout>
        </div>
      ) : null}

      <Card>
        <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)}>
          <Field
            label="New password"
            type="password"
            name="password"
            autoComplete="new-password"
            required
            hint={PASSWORD_HINT}
            value={password}
            onChange={(e) => {
              setPassword(e.currentTarget.value)
              // The cell's verdict was about the string that was submitted; once it changes the
              // verdict is stale, and one left standing under a fixed field is its own dead end.
              setPasswordFailure(null)
            }}
            {...(passwordError ? { error: passwordError } : {})}
          />
          <Button
            type="submit"
            disabled={tooShort || password.length === 0 || state.kind === 'saving' || !config.ok}
            className="mt-1 w-full"
          >
            {state.kind === 'saving' ? 'Saving…' : 'Set new password'}
          </Button>
        </form>
      </Card>
    </AuthLayout>
  )
}
